import type { JSONSchemaType } from 'ajv'
import type { AutomationActionType } from '../../../types/automation'
import type { ActionContext, ActionDefinition, ActionOutcome } from '../action'
import { getAction, registerAction } from '../registry'
import type { RegistryField } from '../../automationRegistry'

type AutomationArgs = Record<string, unknown>

type AutomationActionSpec = {
  type: AutomationActionType
  id: string
  label: string
  description: string
  external: boolean
  fields: RegistryField[]
  defaults: Record<string, unknown>
  simulatedOutcomeTemplate: string
}

const fieldPropertySchema = (field: RegistryField): Record<string, unknown> => {
  switch (field.kind) {
    case 'NUMBER':
      return { type: 'number' }
    case 'TOGGLE':
      return { type: 'boolean' }
    case 'SELECT':
      return field.options?.length
        ? { type: 'string', enum: field.options.map((option) => option.value) }
        : { type: 'string' }
    case 'TEXT':
    case 'TEXTAREA':
      return { type: 'string' }
    default:
      return { type: 'string' }
  }
}

const argsSchemaFor = (fields: RegistryField[]): JSONSchemaType<AutomationArgs> => ({
  type: 'object',
  properties: Object.fromEntries(fields.map((field) => [field.key, fieldPropertySchema(field)])),
  required: fields.filter((field) => field.required).map((field) => field.key),
  additionalProperties: true,
}) as unknown as JSONSchemaType<AutomationArgs>

const metadataOnlyRun = async (_args: AutomationArgs, context: ActionContext): Promise<ActionOutcome> => ({
  kind: 'FAILED',
  message: 'Automation actions execute through the trusted automation engine, not direct client invocation.',
  recoverable: false,
  correlationId: context.correlationId!,
})

const specs: AutomationActionSpec[] = [
  {
    type: 'CREATE_NODE',
    id: 'automation.node.create',
    label: 'Create node',
    description: 'Creates a timeline node in the workspace.',
    external: false,
    fields: [
      { key: 'title', label: 'Title', kind: 'TEXT', required: true },
      {
        key: 'nodeKind',
        label: 'Kind',
        kind: 'SELECT',
        options: [
          { value: 'GOAL', label: 'Goal' },
          { value: 'TASK', label: 'Task' },
          { value: 'SUBTASK', label: 'Subtask' },
        ],
      },
      { key: 'boardKey', label: 'Board (optional)', kind: 'TEXT' },
      { key: 'parentId', label: 'Parent ID (optional)', kind: 'TEXT' },
    ],
    defaults: { nodeKind: 'TASK' },
    simulatedOutcomeTemplate: 'Would create a {nodeKind} titled "{title}" on board {boardKey}.',
  },
  {
    type: 'NOTIFY_USER',
    id: 'automation.user.notify',
    label: 'Notify user',
    description: 'Sends an in-app notification through the workspace notification channel.',
    external: false,
    fields: [
      { key: 'title', label: 'Notification title', kind: 'TEXT', required: true },
      { key: 'body', label: 'Body', kind: 'TEXTAREA' },
      { key: 'recipientUserId', label: 'Recipient user ID (optional)', kind: 'TEXT' },
    ],
    defaults: { title: 'Automation fired' },
    simulatedOutcomeTemplate: 'Would notify {recipient} with "{title}".',
  },
  {
    type: 'UPDATE_FIELD',
    id: 'automation.field.update',
    label: 'Update field',
    description: 'Updates a field on a workspace record.',
    external: false,
    fields: [
      {
        key: 'targetType',
        label: 'Target',
        kind: 'SELECT',
        options: [
          { value: 'NODE', label: 'Timeline node' },
          { value: 'CHECKLIST', label: 'Checklist item' },
          { value: 'FORM', label: 'Form definition' },
        ],
        required: true,
      },
      { key: 'targetId', label: 'Target ID', kind: 'TEXT' },
      { key: 'fieldPath', label: 'Field path', kind: 'TEXT', required: true, placeholder: 'status, dates.end…' },
      { key: 'value', label: 'New value', kind: 'TEXT' },
    ],
    defaults: { targetType: 'NODE' },
    simulatedOutcomeTemplate: 'Would update {targetType}:{targetId}.{fieldPath} to "{value}".',
  },
  {
    type: 'COMPLETE_NODE',
    id: 'automation.node.complete',
    label: 'Complete node',
    description: 'Marks a timeline node as complete on the workspace.',
    external: false,
    fields: [{ key: 'targetId', label: 'Node ID', kind: 'TEXT', required: true, placeholder: '{{payload.nodeId}}' }],
    defaults: {},
    simulatedOutcomeTemplate: 'Would mark node {targetId} as complete.',
  },
  {
    type: 'ASSIGN_TASK',
    id: 'automation.task.assign',
    label: 'Assign task',
    description: 'Assigns a task to a workspace member.',
    external: false,
    fields: [
      { key: 'targetId', label: 'Task ID', kind: 'TEXT', required: true, placeholder: '{{payload.nodeId}}' },
      { key: 'assigneeId', label: 'Assignee user ID', kind: 'TEXT', required: true },
    ],
    defaults: {},
    simulatedOutcomeTemplate: 'Would assign task {targetId} to {assigneeId}.',
  },
  {
    type: 'CREATE_CHECKLIST_ITEM',
    id: 'automation.checklist-item.create',
    label: 'Create checklist item',
    description: 'Adds a checklist item under an existing task.',
    external: false,
    fields: [
      { key: 'parentId', label: 'Parent task ID', kind: 'TEXT', required: true },
      { key: 'title', label: 'Item title', kind: 'TEXT', required: true },
    ],
    defaults: {},
    simulatedOutcomeTemplate: 'Would add checklist item "{title}" under {parentId}.',
  },
  {
    type: 'SEND_CHAT_MESSAGE',
    id: 'automation.chat-message.send',
    label: 'Send chat message',
    description: 'Posts a message into a workspace group-chat channel.',
    external: false,
    fields: [
      { key: 'channelId', label: 'Channel ID', kind: 'TEXT', required: true },
      { key: 'body', label: 'Message body', kind: 'TEXTAREA', required: true },
    ],
    defaults: {},
    simulatedOutcomeTemplate: 'Would post message into {channelId}.',
  },
  {
    type: 'GRANT_CREDITS',
    id: 'automation.credits.grant',
    label: 'Grant wallet credits',
    description: 'Deposits credits into the recipient’s user_ledger wallet. Admin only.',
    external: false,
    fields: [
      { key: 'recipientEmail', label: 'Recipient email', kind: 'TEXT', required: true },
      { key: 'amount', label: 'Amount', kind: 'NUMBER', required: true },
      { key: 'reason', label: 'Reason (audit note)', kind: 'TEXT' },
    ],
    defaults: {},
    simulatedOutcomeTemplate: 'Would deposit {amount} credits to {recipientEmail}.',
  },
  {
    type: 'DEDUCT_CREDITS',
    id: 'automation.credits.deduct',
    label: 'Deduct wallet credits',
    description: 'Removes credits from a wallet. Admin only.',
    external: false,
    fields: [
      { key: 'recipientEmail', label: 'Wallet owner email', kind: 'TEXT', required: true },
      { key: 'amount', label: 'Amount', kind: 'NUMBER', required: true },
      { key: 'reason', label: 'Reason (audit note)', kind: 'TEXT' },
    ],
    defaults: {},
    simulatedOutcomeTemplate: 'Would remove {amount} credits from {recipientEmail}.',
  },
  {
    type: 'AWARD_XP',
    id: 'automation.xp.award',
    label: 'Award XP',
    description: 'Awards experience points to the actor or a specified user.',
    external: false,
    fields: [
      { key: 'recipientUserId', label: 'Recipient user ID (optional — defaults to actor)', kind: 'TEXT' },
      { key: 'amount', label: 'XP amount', kind: 'NUMBER', required: true },
    ],
    defaults: {},
    simulatedOutcomeTemplate: 'Would award {amount} XP to {recipientUserId}.',
  },
  {
    type: 'DELAY',
    id: 'automation.delay.wait',
    label: 'Wait',
    description: 'Pauses the flow. Later actions in the branch run after the delay.',
    external: false,
    fields: [{ key: 'seconds', label: 'Seconds', kind: 'NUMBER', required: true, placeholder: '60' }],
    defaults: { seconds: 60 },
    simulatedOutcomeTemplate: 'Would wait {seconds}s before continuing.',
  },
  {
    type: 'SET_VARIABLE',
    id: 'automation.variable.set',
    label: 'Set variable',
    description: 'Stores a value on the run context so later actions or conditions can read it.',
    external: false,
    fields: [
      { key: 'name', label: 'Variable name', kind: 'TEXT', required: true, placeholder: 'priorityScore' },
      { key: 'value', label: 'Value (string or {{path}})', kind: 'TEXT', required: true },
    ],
    defaults: {},
    simulatedOutcomeTemplate: 'Would set vars.{name} = {value}.',
  },
  {
    type: 'LOG_TO_LEDGER',
    id: 'automation.ledger.log',
    label: 'Log to activity ledger',
    description: 'Writes an audit entry to activityLedger for compliance visibility.',
    external: false,
    fields: [
      { key: 'category', label: 'Category', kind: 'TEXT', required: true, placeholder: 'AUTOMATION' },
      { key: 'message', label: 'Message', kind: 'TEXTAREA', required: true },
    ],
    defaults: { category: 'AUTOMATION' },
    simulatedOutcomeTemplate: 'Would log [{category}] "{message}".',
  },
  {
    type: 'ADVANCE_BOOK_WINDOW',
    id: 'automation.book-window.advance',
    label: 'Advance recurring book window',
    description: 'Rolls the daily/weekly pages of a recurring book. Managed automatically by the Checklists → Recurring hub.',
    external: false,
    fields: [
      { key: 'bookId', label: 'Book ID', kind: 'TEXT', required: true, helpText: 'Do not edit — the linked book is the source of truth.' },
    ],
    defaults: {},
    simulatedOutcomeTemplate: 'Would advance the rolling window of book {bookId}.',
  },
  {
    type: 'WEBHOOK',
    id: 'automation.webhook.call',
    label: 'External webhook',
    description: 'Calls an external URL. Costs credits and requires a linked provider connection.',
    external: true,
    fields: [
      {
        key: 'providerId',
        label: 'Provider',
        kind: 'SELECT',
        required: true,
        options: [
          { value: 'GENERIC_WEBHOOK', label: 'Generic webhook' },
          { value: 'NOTION', label: 'Notion' },
          { value: 'G_DRIVE', label: 'Google Drive' },
          { value: 'JIRA', label: 'Jira' },
        ],
      },
      { key: 'connectionId', label: 'Connection ID', kind: 'TEXT', helpText: 'Required for generic webhooks with multiple saved endpoints.' },
      { key: 'url', label: 'Endpoint URL (OAuth providers)', kind: 'TEXT', placeholder: 'https://…' },
      {
        key: 'method',
        label: 'Method',
        kind: 'SELECT',
        options: [
          { value: 'POST', label: 'POST' },
          { value: 'GET', label: 'GET' },
          { value: 'PUT', label: 'PUT' },
          { value: 'PATCH', label: 'PATCH' },
          { value: 'DELETE', label: 'DELETE' },
        ],
      },
      { key: 'bodyTemplate', label: 'Body template', kind: 'TEXTAREA' },
    ],
    defaults: { method: 'POST', providerId: 'GENERIC_WEBHOOK' },
    simulatedOutcomeTemplate: 'Would {method} the event payload to {url} via {providerId}.',
  },
]

const toDefinition = (spec: AutomationActionSpec): ActionDefinition<AutomationArgs> => ({
  id: spec.id,
  label: spec.label,
  description: spec.description,
  risk: spec.external ? 'MEDIUM' : 'LOW',
  domain: 'automation',
  surfaces: ['automate'],
  argsSchema: argsSchemaFor(spec.fields),
  run: metadataOnlyRun,
  automate: {
    actionType: spec.type,
    fields: spec.fields,
    defaults: spec.defaults,
    simulatedOutcomeTemplate: spec.simulatedOutcomeTemplate,
    external: spec.external,
  },
})

export const AUTOMATION_ACTION_DEFINITIONS = specs.map(toDefinition)

export const registerAutomationActions = (): void => {
  for (const definition of AUTOMATION_ACTION_DEFINITIONS) {
    if (!getAction(definition.id)) registerAction(definition)
  }
}

registerAutomationActions()
